import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import uuid from 'uuid/v4';
import { useDispatch, useSelector } from 'react-redux';
import { unreachable } from 'hellospa/ts-utils';
import 'libphonenumber-wrapper';
import { StoreShape } from 'hellospa/redux/namespaces';
import { Button, Wrapper, Menu, MenuItem, openMenu, closeMenu } from 'hellospa/components/filterable-dropdown';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import styles from './styles.scss';
import { Contact } from 'hellospa/page/prep-and-send/data/types';
import ImportModal from './import-contacts';
import { trackHeapCustomEvent } from 'common/utils/heap';

type LibPhoneNumberResult = {
  'country': string,
  'number': string,
}

type Props = {
  value: string,
  onChange: (value: string) => void,
  onSelectContact: (contact: Contact) => void
  contactTypes: Contact['type'][],
  searchEnabled?: boolean,
  id: string,
  isSmsInput?: boolean,
}

const phoneRegex = /^[\d+\s-]+$/;

const phoneUtil = i18n.phonenumbers.PhoneNumberUtil.getInstance();

function useLibPhoneNumber(input: string, active: boolean): LibPhoneNumberResult[] {
  /* global i18n */

  return React.useMemo(() => {
    if (active && input.match(phoneRegex)) {
      return phoneUtil.getFormattedPhoneOptions(input.replace(/^\+/, ''), phoneUtil);
    }
    return [];
  }, [active, input]);
}

function useContactSearch(input: string, contactTypes: Props['contactTypes'], enabled: boolean): Contact[] {
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (enabled) {
      dispatch(
        actions.loadContacts(),
      );
    }
  });

  return useSelector(
    (state: StoreShape) => selectors.findContactsByName(state, input, contactTypes),
  );
}

type ContactItem = {
  type: 'contact'
  item: Contact,
}

type FaxItem = {
  type: 'fax',
  item: LibPhoneNumberResult
}
type PricingItem = {
  type: 'pricing'
}
type ImportContactsItem = {
  type: 'import-contacts'
}
type MenuItemTypes = ContactItem | FaxItem | PricingItem | ImportContactsItem

type GenericFieldHTMLAttributes = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange'
>;

export default function ContactInput({
  value, onChange, onSelectContact, contactTypes = ['E', 'F'], id, className, searchEnabled = true, isSmsInput = false, ...props
}: GenericFieldHTMLAttributes & Props) {
  const [changed, setChanged] = React.useState(false);
  const [isModalOpen, setModalOpen] = React.useState(false);
  const wrapperId: string = React.useMemo(uuid, []);
  // hooks can't be called conditionally, so this one gets an `active` flag
  const phoneNumbers = useLibPhoneNumber(value, contactTypes.includes('F'));
  const isHungary = phoneUtil.checkIsHungary(value);
  const contacts = useContactSearch(value, contactTypes, searchEnabled);
  const isDeepIntegration = useSelector(selectors.isDeepIntegration);
  const isHelloFax = useSelector(selectors.isHelloFax);

  const country = React.useMemo(() => {
    if (phoneNumbers.length > 0) {
      if (isHungary) {
        return 'Hungary';
      }

      return phoneNumbers[0].country;
    }
    return null;
  }, [phoneNumbers, isHungary]);


  const handleChange = React.useCallback((e) => {
    setChanged(true);
    onChange(e.target.value);
  }, [onChange]);

  const handleBlur = React.useCallback((e) => {
    if (phoneNumbers.length > 0) {
      let idx = 0;

      // Use the Hungary number if it's provided in the list, overwise default to first
      // option
      if (isHungary) {
        idx = phoneNumbers.findIndex((item) => item.country === 'Hungary');
        idx = idx >= 0 ? idx : 0;
      }

      const autoFormat = phoneNumbers[idx].number;
      onChange(autoFormat);
    }

    if (props.onBlur) {
      props.onBlur(e);
    }
  }, [onChange, phoneNumbers, props, isHungary]);

  const pricingLink = React.useRef<HTMLAnchorElement>(null);
  const handleSelection = React.useCallback((
    selection: MenuItemTypes,
  ) => {
    if (selection.type === 'pricing') {
      pricingLink.current!.click();
    } else if (selection.type === 'import-contacts') {
      trackHeapCustomEvent('Import Contacts Clicked');
      setModalOpen(true);
    } else if (selection.type === 'contact') {
      setChanged(false);
      onSelectContact(selection.item);
    } else if (selection.type === 'fax') {
      setChanged(false);
      onChange(selection.item.number);
    } else {
      unreachable(selection);
    }
  }, [onChange, onSelectContact]);


  let menuItems: React.ReactNode[] = [];
  let hideArrow = true;
  if (contacts.length > 0) {
    hideArrow = false;
    menuItems = contacts.map((c) => (
      <MenuItem tag="li" key={c.id} value={{ type: 'contact', item: c }} className={classnames({
        [styles.activeMenuItem]: c.value === value,
      })}
      >
        &quot;{c.name}&quot; {c.value}
      </MenuItem>
    ));
    if (contacts.length === 1 && value === contacts[0].value) {
      menuItems = [];
    }
  }

  let autoOpenMenu = true;
  if (menuItems.length === 0 && phoneNumbers.length > 1) {
    hideArrow = false;
    autoOpenMenu = false;
    menuItems = phoneNumbers.map((result) => (
      <MenuItem tag="li" key={result.country} value={{ type: 'fax', item: result }} className={classnames({
        [styles.activeMenuItem]: result.number === value,
      })}
      >
        {result.number} - {result.country}
      </MenuItem>
    ));

    if (!isSmsInput) {
      menuItems.splice(1, 0, (
        <MenuItem tag="li" key="pricing" value={{ type: 'pricing' }} className={styles.pricingLink}>
          <a
            ref={pricingLink}
            target="_blank"
            rel="noopener noreferrer"
            href="http://www.hellofax.com/info/internationalFaxPricing" />
          <FormattedMessage
            id=""
            defaultMessage="Click to see all countries we support"
            description="Link to our internaional fax pricing page" />

        </MenuItem>
      ));
    }
  }

  if (searchEnabled && !isDeepIntegration
    && value.length > 0 && phoneNumbers.length === 0 && changed) {
    menuItems.push((
      <MenuItem tag="li" key="import-contacts" value={{ type: 'import-contacts' }}>
        <FormattedMessage
          id=""
          defaultMessage="Import Contacts"
          description="Menu item to open an Impor Contacts modal" />
      </MenuItem>
    ));
  }

  React.useEffect(() => {
    if (autoOpenMenu && value.length > 0 && menuItems.length > 0) {
      openMenu(wrapperId, { focusMenu: false });
    } else {
      closeMenu(wrapperId, { focusButton: false });
    }
  }, [autoOpenMenu, menuItems.length, value.length, wrapperId]);

  return (
    <React.Fragment>
      <Wrapper id={wrapperId} onSelection={handleSelection} className={className}>
        <Button
          id={id}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          hideArrow={hideArrow}
          inputProps={{
            ...props,
            onBlur: handleBlur,
          }}
        >
          <span className={classnames(styles.country, { [styles.hide]: !country })}>
            {country}
          </span>
        </Button>
        <Menu tag="ul">{menuItems}</Menu>
      </Wrapper>
      <ImportModal
        isOpen={isModalOpen}
        isHelloFax={isHelloFax}
        onRequestClose={() => setModalOpen(false)} />
    </React.Fragment>
  );
}
